function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useContext, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import cn from 'classnames';
import get from 'lodash/get';
import { ProfileContext } from 'context';
import { getSlug } from 'helpers/urlTools';
import { useAction } from 'hooks';
import Button from 'components/Form/Button';
import Account from 'modules/Account';
import Hcp from 'modules/Hcp';
import { useIsDisabled } from '../../../../hooks';
import * as actions from '../../../../actions';
import * as selectors from '../../../../selectors';
import messages from '../../../../messages';
import styles from '../../DownloadDataModal.pcss';
import { useIsProfileActivationInProgress } from '../hooks';
import ResultsTableSegment from '../ResultsTableSegment';
import { useStoreReadings } from './hooks';
const Summary = ()=>{
    const deviceData = useSelector(selectors.deviceData);
    const logEvent = useAction(actions.logEvent, 'DataPulled');
    useEffect(()=>{
        logEvent();
    }, []);
    return /*#__PURE__*/ _jsxs("div", {
        className: "row no-gutters align-items-center mt-6",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "col p-6 mr-6",
                children: [
                    /*#__PURE__*/ _jsx("h3", {
                        className: "text--small text--bold mb-3",
                        children: deviceData.deviceName
                    }),
                    /*#__PURE__*/ _jsxs("p", {
                        className: "text--small text--light text--nowrap",
                        children: [
                            "SN: ",
                            deviceData.serialNumber
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: `col-auto p-6 ${styles.highlightedRegion}`,
                children: /*#__PURE__*/ _jsxs("p", {
                    className: "row no-gutters align-items-center",
                    children: [
                        /*#__PURE__*/ _jsx("span", {
                            className: "col mr-4",
                            children: /*#__PURE__*/ _jsx(FormattedMessage, _object_spread({}, messages.labels.readingsNumber))
                        }),
                        /*#__PURE__*/ _jsx("span", {
                            className: "col-auto text--large text--bold",
                            children: deviceData.summary.readingsCount
                        })
                    ]
                })
            })
        ]
    });
};
const TargetGlucoseCount = ()=>{
    const deviceData = useSelector(selectors.deviceData);
    const lowIcon = /*#__PURE__*/ _jsx("i", {
        className: `bg--error ${styles.results__disc}`
    });
    const targetIcon = /*#__PURE__*/ _jsx("i", {
        className: `bg--success ${styles.results__disc}`
    });
    const highIcon = /*#__PURE__*/ _jsx("i", {
        className: `bg--warning ${styles.results__disc}`
    });
    const { lowCount, targetCount, highCount } = deviceData.summary;
    return /*#__PURE__*/ _jsxs("div", {
        className: styles.strippedTable,
        children: [
            /*#__PURE__*/ _jsx(ResultsTableSegment, {
                labelMessage: messages.labels.low,
                value: lowCount,
                icon: lowIcon
            }),
            /*#__PURE__*/ _jsx(ResultsTableSegment, {
                labelMessage: messages.labels.target,
                value: targetCount,
                icon: targetIcon
            }),
            /*#__PURE__*/ _jsx(ResultsTableSegment, {
                labelMessage: messages.labels.high,
                value: highCount,
                icon: highIcon
            })
        ]
    });
};
const ResultsCount = ()=>{
    const deviceData = useSelector(selectors.deviceData);
    if (!deviceData.summary.highNorm || !deviceData.summary.lowNorm) {
        return null;
    }
    return /*#__PURE__*/ _jsx("div", {
        className: `${styles.strippedTable} mt-6`,
        children: /*#__PURE__*/ _jsx(ResultsTableSegment, {
            labelMessage: messages.labels.bgm,
            value: deviceData.summary.readingsCount,
            SubSegment: TargetGlucoseCount
        })
    });
};
const PreviewData = ()=>{
    const profileContext = useContext(ProfileContext);
    const patientProfile = useSelector(profileContext.selectors.activePatient);
    const deviceData = useSelector(selectors.deviceData);
    const activeProfileType = useSelector(Account.selectors.activeProfileType);
    const isHcp = activeProfileType === Account.constants.PROFILE_TYPES.HCP;
    if (!isHcp) {
        return null;
    }
    const activeClinicMembership = useSelector(Account.selectors.activeClinicMembership);
    const { organizationUID, name: clinicName } = get(activeClinicMembership, 'clinic', {});
    const clinicSlug = getSlug(clinicName);
    const onPreviewPatient = useAction(Hcp.actions.previewPatientOpenTab, deviceData, patientProfile, clinicSlug, organizationUID);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: cn(styles.divider, 'mt-0'),
                children: /*#__PURE__*/ _jsx("span", {
                    className: styles.divider__text,
                    children: /*#__PURE__*/ _jsx(FormattedMessage, _object_spread({}, messages.infos.or))
                })
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "row justify-content-center",
                children: /*#__PURE__*/ _jsx(Button, {
                    styleModifier: "transparent",
                    labelMessage: messages.buttons.previewResults,
                    className: "btn--no-size text--primary m-0 pl-2 pr-2",
                    onClick: onPreviewPatient
                })
            })
        ]
    });
};
const Actions = ({ onCancel })=>{
    const profileContext = useContext(ProfileContext);
    const deviceData = useSelector(selectors.deviceData);
    const patientProfile = useSelector(profileContext.selectors.activePatient);
    const isProfileActivationInProgress = useIsProfileActivationInProgress();
    const isDisabled = useIsDisabled(patientProfile, isProfileActivationInProgress);
    const { isInProgress, onImport } = useStoreReadings(patientProfile, onCancel);
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "row mt-5",
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: "col-6",
                        children: /*#__PURE__*/ _jsx(Button, {
                            styleModifier: "primary",
                            labelMessage: messages.buttons.cancelImport,
                            className: "btn--block",
                            isDisabled: isInProgress,
                            onClick: onCancel
                        })
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "col-6",
                        children: /*#__PURE__*/ _jsx(Button, {
                            styleModifier: "primary",
                            labelMessage: messages.buttons.importResults,
                            className: "btn--block btn--filled",
                            isDisabled: isDisabled || !deviceData.summary.readingsCount,
                            isInProgress: isInProgress,
                            onClick: onImport
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(PreviewData, {})
        ]
    });
};
const BgmResults = (props)=>/*#__PURE__*/ _jsxs("div", {
        children: [
            /*#__PURE__*/ _jsx(Summary, {}),
            /*#__PURE__*/ _jsx(ResultsCount, {}),
            /*#__PURE__*/ _jsx(Actions, _object_spread({}, props))
        ]
    });
export default BgmResults;
